import { ReactElement, useState } from 'react';
import { Link as RSLink } from 'react-scroll';

import { MenuOutlined as MenuOutlinedIcon } from '@mui/icons-material';
import { useTheme, Popper, ClickAwayListener, Box, Link as MUILink, Collapse } from '@mui/material';

import { Anchor, Event, MenuProps } from './types';

import IconButton from '../../../../../../components/IconButton';
import navItems from '../../data';
import { NavItem } from '../../types';
import ListItem from '../ListItem';

function Child({ label, children }: NavItem): ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <>
      {children ? (
        <Box onClick={() => setIsOpen(!isOpen)}>
          <ListItem label={label} hasChildren isOpen={isOpen} />
        </Box>
      ) : (
        <ListItem label={label} isOpen={isOpen} />
      )}

      {children ? (
        <Collapse in={isOpen} unmountOnExit>
          <Box
            sx={{
              width: '100%',

              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'stretch'
            }}>
            {children.map((child) => (
              <RSLink
                key={child.label}
                aria-label={`${child.label} nav-item`}
                to={child.to || ''}
                spy
                smooth
                offset={-99.19}>
                <ListItem label={child.label} color={child.color} isChild />
              </RSLink>
            ))}
          </Box>
        </Collapse>
      ) : null}
    </>
  );
}

export default function Menu({ invert }: MenuProps): ReactElement {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<Anchor>(null);

  const handleOpenMenu = (event: Event): void => {
    setAnchorEl(!anchorEl ? event.currentTarget : null);
  };

  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };

  return (
    <ClickAwayListener onClickAway={() => handleCloseMenu()}>
      <div>
        <IconButton
          aria-label='Open menu'
          aria-controls='nav-menu'
          aria-haspopup='true'
          color='secondary'
          variant='icon'
          onClick={handleOpenMenu}
          sx={!invert ? { color: `${theme.palette.grey[50]} !important` } : {}}>
          <MenuOutlinedIcon />
        </IconButton>

        <Popper
          className='nav-menu'
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement='bottom-end'
          style={{
            width: '100%',

            zIndex: 1000,

            backgroundColor: 'transparent',
            borderRadius: 0,

            padding: 0
          }}>
          <Box
            sx={{
              width: '100%',

              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'stretch',

              backgroundColor: theme.palette.background.default,

              my: 2.5
            }}>
            {navItems.map((navItem) =>
              navItem.href ? (
                <MUILink
                  key={navItem.label}
                  aria-label={`${navItem.label} nav-item`}
                  href={navItem.href}
                  underline='none'>
                  <Child {...navItem} />
                </MUILink>
              ) : (
                <RSLink
                  key={navItem.label}
                  aria-label={`${navItem.label} nav-item`}
                  to={navItem.to || ''}
                  spy
                  smooth
                  offset={-99.19}>
                  <Child {...navItem} />
                </RSLink>
              )
            )}
          </Box>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
